import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { lumi } from '../lib/lumi';
import { toast } from 'react-hot-toast';
import {Save, Send, ChevronLeft, Check, Camera, Map, Activity, Zap, Sprout, LifeBuoy} from 'lucide-react';

const serviceImages = {
  photo: "https://static.lumi.new/a3/a31b91225f938abbf513644e077e3679.png",
  event: "https://static.lumi.new/bc/bcc875b66168b9451925519f3bf94b51.png",
  map: "https://static.lumi.new/dc/dcef7e89dc6a74d46fa22769604f1da0.png",
  inspect: "https://static.lumi.new/68/68bf6955d7e5c69569b42f59a651e81e.png",
  agri: "https://static.lumi.new/d6/d6b920a40239b1ba26f88b9a6a1f1057.png",
  rescue: "https://static.lumi.new/52/526d1fff35441fd4f124ab47977ea5ca.png",
};

const ServiceRequestForm = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const servicesList = [
    { id: 'photo', icon: <Camera className="h-5 w-5" />, price: '$250 – $300' },
    { id: 'event', icon: <Activity className="h-5 w-5" />, price: '$250 – $300' },
    { id: 'map', icon: <Map className="h-5 w-5" />, price: '$300 – $400' },
    { id: 'inspect', icon: <Zap className="h-5 w-5" />, price: '$350 – $500' },
    { id: 'agri', icon: <Sprout className="h-5 w-5" />, price: '$250 – $350' },
    { id: 'rescue', icon: <LifeBuoy className="h-5 w-5" />, price: '$400 – $500' }
  ];

  const [formData, setFormData] = useState({
    serviceTypes: [] as string[],
    email: user?.email || '',
    phone: '',
    address: '',
    city: '',
    country: 'Ghana',
    additionalDetails: ''
  });

  const handleServiceToggle = (id: string) => {
    setFormData(prev => {
      const exists = prev.serviceTypes.includes(id);
      if (exists) {
        return { ...prev, serviceTypes: prev.serviceTypes.filter(s => s !== id) };
      } else {
        return { ...prev, serviceTypes: [...prev.serviceTypes, id] };
      }
    });
  };

  const handleSubmit = async (e: React.FormEvent, status: 'draft' | 'submitted') => {
    e.preventDefault();
    if (formData.serviceTypes.length === 0) {
      toast.error(t('form.selectOneService'));
      return;
    }

    setIsSubmitting(true);
    const loadingToast = toast.loading(status === 'draft' ? t('form.savingDraft') : t('form.submittingRequest'));

    try {
      const requestData = {
        userId: user?.userId || 'guest',
        serviceTypes: formData.serviceTypes,
        contactName: user?.userName || formData.email.split('@')[0],
        contactEmail: formData.email,
        phone: formData.phone,
        address: formData.address,
        city: formData.city,
        country: formData.country,
        additionalDetails: formData.additionalDetails,
        status,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      // @ts-ignore
      await lumi.entities.ServiceRequests.create(requestData);

      if (status === 'submitted') {
        try {
          await lumi.tools.email.send({
            to: formData.email,
            subject: t('form.emailSubject'),
            html: `
              <h1>${t('form.emailThankYou')}</h1>
              <p>${t('form.emailDear')} ${user?.userName || formData.email.split('@')[0]},</p>
              <p>${t('form.emailReceived')}</p>
              <ul>
                ${formData.serviceTypes.map(id => `<li>${t(`service.${id}.title`)}</li>`).join('')}
              </ul>
              ${formData.additionalDetails ? `<p><strong>Additional Details:</strong> ${formData.additionalDetails}</p>` : ''}
              <p>${t('form.emailReview')} ${formData.phone}.</p>
              <br>
              <p>${t('form.emailBestRegards')}<br>${t('form.emailTeam')}</p>
            `
          });
          toast.success(t('form.confirmationEmailSent'));
        } catch (emailError) {
          console.error('Email failed:', emailError);
        }
        
        toast.success(t('form.requestSubmitted'), { id: loadingToast });
        navigate('/dashboard');
      } else {
        toast.success(t('form.draftSaved'), { id: loadingToast });
        navigate('/dashboard');
      }

    } catch (error) {
      console.error('Submission error:', error);
      toast.error(t('form.requestFailed'), { id: loadingToast });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 py-12">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {user && (
          <button 
            onClick={() => navigate('/dashboard')}
            className="flex items-center text-slate-500 hover:text-slate-900 mb-6 transition-colors"
          >
            <ChevronLeft className="h-4 w-4 mr-1" />
            {t('form.backToDashboard')}
          </button>
        )}

        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
          <div className="bg-slate-900 px-8 py-6">
            <h1 className="text-2xl font-bold text-white">{t('form.newRequest')}</h1>
            <p className="text-slate-400 mt-1">{t('form.subtitle')}</p>
          </div>

          <form className="p-8 space-y-8">
            {/* Service Selection with Images */}
            <div>
              <h3 className="text-lg font-semibold text-slate-900 mb-4">1. {t('form.selectServices')}</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {servicesList.map((service) => (
                  <button
                    key={service.id}
                    type="button"
                    onClick={() => handleServiceToggle(service.id)}
                    className={`group relative overflow-hidden rounded-xl border-2 text-left transition-all ${
                      formData.serviceTypes.includes(service.id)
                        ? 'border-sky-500 ring-2 ring-sky-200'
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    {/* Service Image */}
                    <div className="h-32 overflow-hidden">
                      <img 
                        src={serviceImages[service.id as keyof typeof serviceImages]} 
                        alt={t(`service.${service.id}.title`)}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 to-transparent" />
                    </div>

                    {/* Service Info */}
                    <div className="p-4">
                      <div className="flex items-start justify-between mb-2">
                        <div className={`p-2 rounded-lg ${
                          formData.serviceTypes.includes(service.id) ? 'bg-sky-100 text-sky-600' : 'bg-slate-100 text-slate-600'
                        }`}>
                          {service.icon}
                        </div>
                        <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                          formData.serviceTypes.includes(service.id) 
                            ? 'border-sky-500 bg-sky-500' 
                            : 'border-slate-300 bg-white'
                        }`}>
                          {formData.serviceTypes.includes(service.id) && <Check className="h-4 w-4 text-white" />}
                        </div>
                      </div>
                      <h4 className="font-semibold text-sm text-slate-900 mb-1">{t(`service.${service.id}.title`)}</h4>
                      <p className="text-xs text-slate-600 mb-2">{t(`service.${service.id}.desc`)}</p>
                      <div className="flex items-center justify-between pt-2 border-t border-slate-100">
                        <span className="text-xs text-slate-500">{t('pricing.per_hour')}</span>
                        <span className="text-sm font-bold text-sky-600">{service.price}</span>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Additional Details */}
            <div>
              <h3 className="text-lg font-semibold text-slate-900 mb-4">2. {t('form.additionalDetails')}</h3>
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">{t('form.additionalDetailsLabel')}</label>
                <textarea
                  value={formData.additionalDetails}
                  onChange={e => setFormData({...formData, additionalDetails: e.target.value})}
                  rows={4}
                  className="w-full px-4 py-3 rounded-lg border border-slate-200 focus:border-sky-500 focus:ring-2 focus:ring-sky-200 outline-none transition-all resize-none"
                  placeholder={t('form.additionalDetailsPlaceholder')}
                />
              </div>
            </div>

            {/* Contact Details */}
            <div>
              <h3 className="text-lg font-semibold text-slate-900 mb-4">3. {t('form.contactLocation')}</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-700">{t('form.emailAddress')}</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={e => setFormData({...formData, email: e.target.value})}
                    className="w-full px-4 py-2 rounded-lg border border-slate-200 focus:border-sky-500 focus:ring-2 focus:ring-sky-200 outline-none transition-all"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-700">{t('form.phoneNumber')}</label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={e => setFormData({...formData, phone: e.target.value})}
                    className="w-full px-4 py-2 rounded-lg border border-slate-200 focus:border-sky-500 focus:ring-2 focus:ring-sky-200 outline-none transition-all"
                    placeholder="+233..."
                    required
                  />
                </div>
                <div className="col-span-full space-y-2">
                  <label className="text-sm font-medium text-slate-700">{t('form.streetAddress')}</label>
                  <input
                    type="text"
                    value={formData.address}
                    onChange={e => setFormData({...formData, address: e.target.value})}
                    className="w-full px-4 py-2 rounded-lg border border-slate-200 focus:border-sky-500 focus:ring-2 focus:ring-sky-200 outline-none transition-all"
                    placeholder={t('form.addressPlaceholder')}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-700">{t('form.city')}</label>
                  <input
                    type="text"
                    value={formData.city}
                    onChange={e => setFormData({...formData, city: e.target.value})}
                    className="w-full px-4 py-2 rounded-lg border border-slate-200 focus:border-sky-500 focus:ring-2 focus:ring-sky-200 outline-none transition-all"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-700">{t('form.country')}</label>
                  <input
                    type="text"
                    value={formData.country}
                    onChange={e => setFormData({...formData, country: e.target.value})}
                    className="w-full px-4 py-2 rounded-lg border border-slate-200 focus:border-sky-500 focus:ring-2 focus:ring-sky-200 outline-none transition-all"
                    readOnly
                  />
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="pt-6 border-t border-slate-100 flex items-center justify-end gap-4">
              <button
                type="button"
                onClick={(e) => handleSubmit(e, 'draft')}
                disabled={isSubmitting}
                className="flex items-center gap-2 px-6 py-3 rounded-lg border border-slate-200 text-slate-700 font-medium hover:bg-slate-50 transition-colors disabled:opacity-50"
              >
                <Save className="h-4 w-4" />
                {t('form.saveDraft')}
              </button>
              <button
                type="button"
                onClick={(e) => handleSubmit(e, 'submitted')}
                disabled={isSubmitting}
                className="flex items-center gap-2 px-6 py-3 rounded-lg bg-sky-600 text-white font-medium hover:bg-sky-500 transition-colors shadow-lg shadow-sky-900/20 disabled:opacity-50"
              >
                {isSubmitting ? t('form.processing') : t('form.submitRequest')}
                <Send className="h-4 w-4" />
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ServiceRequestForm;